/**
 * Resource types — V109-2-1 / ADR-0088 §1 (migration 653).
 *
 * The closed list of resource types a discovery source may record. A resource is the
 * configuration scope a control is applied to (a GCP project or organization, a Cloudflare
 * account, a D1 database); finer objects a rule inspects are observation elements, not
 * resources. Each type names the product-level inventory item it is an instance of
 * (ADR-0072 §1) and the asset tier its `assets` row gets when it is linked (ADR-0057).
 *
 * A type not on this list is never guessed: the discovery source records an unresolved clue
 * (migration 655) that a person resolves or dismisses.
 *
 * Pure; no I/O.
 */
export const INFRA_RESOURCE_TYPES = ['gcp.organization', 'gcp.project', 'cloudflare.account', 'cloudflare.d1_database'];
const GCP_PROJECT_ID = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;
export const INFRA_RESOURCE_TYPE_SPECS = Object.freeze({
    'gcp.organization': { type: 'gcp.organization', provider_key: 'gcp', product: '*', asset_tier: 'other', label: 'Google Cloud organization', prefix: 'gcp:organizations/', native: /^\d{1,20}$/ },
    'gcp.project': { type: 'gcp.project', provider_key: 'gcp', product: '*', asset_tier: 'other', label: 'Google Cloud project', prefix: 'gcp:projects/', native: GCP_PROJECT_ID },
    'cloudflare.account': { type: 'cloudflare.account', provider_key: 'cloudflare', product: '*', asset_tier: 'other', label: 'Cloudflare account', prefix: 'cloudflare:accounts/', native: /^[0-9a-f]{32}$/ },
    'cloudflare.d1_database': {
        type: 'cloudflare.d1_database', provider_key: 'cloudflare', product: 'd1', asset_tier: 'database', label: 'D1 database', prefix: 'cloudflare:d1/',
        native: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
    },
});
export function isInfraResourceType(v) {
    return typeof v === 'string' && INFRA_RESOURCE_TYPES.includes(v);
}
export function resourceTypeSpec(type) {
    return INFRA_RESOURCE_TYPE_SPECS[type];
}
/**
 * The normalized `resource_key` for a native id, or null when the id is not valid for the
 * type. Hex ids are compared lower-case so one database never becomes two resources.
 */
export function resourceKeyFor(type, nativeId) {
    const spec = INFRA_RESOURCE_TYPE_SPECS[type];
    const id = (nativeId ?? '').trim();
    const norm = type === 'cloudflare.account' || type === 'cloudflare.d1_database' ? id.toLowerCase() : id;
    return spec.native.test(norm) ? `${spec.prefix}${norm}` : null;
}
/** The native id of a valid key of the given type (inverse of resourceKeyFor), else null. */
export function nativeIdOf(type, resourceKey) {
    const spec = INFRA_RESOURCE_TYPE_SPECS[type];
    if (!resourceKey.startsWith(spec.prefix))
        return null;
    const id = resourceKey.slice(spec.prefix.length);
    return spec.native.test(id) ? id : null;
}
/** A GCP connection scope (`projects/<id>` | `organizations/<n>`) → its resource type and key. */
export function gcpScopeResource(scope) {
    if (typeof scope !== 'string')
        return null;
    const p = /^projects\/(.+)$/.exec(scope.trim());
    if (p) {
        const key = resourceKeyFor('gcp.project', p[1]);
        return key ? { type: 'gcp.project', key, native: p[1] } : null;
    }
    const o = /^organizations\/(.+)$/.exec(scope.trim());
    if (o) {
        const key = resourceKeyFor('gcp.organization', o[1]);
        return key ? { type: 'gcp.organization', key, native: o[1] } : null;
    }
    return null;
}
/**
 * The project id inside a Cloud Asset Inventory full resource name
 * (`//compute.googleapis.com/projects/<id>/zones/…`), or null. Project NUMBERS are not
 * returned: the connection scope names projects by id, and a number would make the same
 * project a second resource.
 */
export function gcpProjectOfAssetName(name) {
    const m = /\/projects\/([^/]+)(?:\/|$)/.exec(name ?? '');
    return m && GCP_PROJECT_ID.test(m[1]) ? m[1] : null;
}
//# sourceMappingURL=infra-resource-types.js.map